import { isWarehouseWhoWereReachingSnapshot } from "@act/shared/data-transfer-objects";
import { BarChart, Bar, XAxis, YAxis, LabelList, Rectangle } from "recharts";
import SectionReveal from "./SectionReveal";
import SectionBanner from "./SectionBanner";
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import { border, body, muted } from "./tokens";
import { FONT_BODY, FONT_DISPLAY, TEXT_PRIMARY, TEXT_SECONDARY } from "./BrandChart";
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { useReportChartDimensions } from '../../hooks/useReportChartDimensions.hook';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';
import { readHomeVisitsChartData } from './programGoalsChartData.util';

const HOME_VISITS_CHART_HEIGHT = 400;
const HOME_VISITS_PRINT_CHART_HEIGHT = 260;

const homeVisitsBarChartLayout = (printStatic: boolean) =>
  printStatic
    ? {
        margin: { top: 22, right: 0, left: 0, bottom: 2 },
        barGap: 3,
        barCategoryGap: 16,
        barSize: 40,
        tickFontSize: 14,
        labelFontSize: 15,
        xAxisPadding: { left: 140, right: 140 },
      }
    : {
        margin: { top: 24, right: 8, bottom: 24, left: 8 },
        barGap: 2,
        barCategoryGap: '18%',
        barSize: 32,
        tickFontSize: 13,
        labelFontSize: 14,
        xAxisPadding: { left: 0, right: 0 },
      };

const STATUS_CONFIG = [
  { key: "inProgram", label: "In Program", color: "var(--reema-midnight-100)" },
  { key: "completed", label: "Completed", color: "#3E7A6B" },
  { key: "refused", label: "Refused", color: "var(--reema-coral-100)" },
] as const;

const SliverBar = (props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  value?: number;
}) => {
  const { x = 0, y = 0, width = 0, height = 0, fill, value } = props;
  const sliverHeight = value && value > 0 ? height : 20;
  const sliverY = value && value > 0 ? y : y + height - sliverHeight;
  return (
    <Rectangle
      x={x}
      y={sliverY}
      width={width}
      height={sliverHeight}
      fill={fill}
      radius={[4, 4, 0, 0]}
    />
  );
};

const HomeVisits = ({ data }: ReportHostedSectionProps) => {
  const chartData = readHomeVisitsChartData(data);
  const chartSource = isWarehouseWhoWereReachingSnapshot(data)
    ? "warehouse"
    : "mock";
  const printStatic = useReportsPrintStaticLayout();
  const chartHeight = printStatic ? HOME_VISITS_PRINT_CHART_HEIGHT : HOME_VISITS_CHART_HEIGHT;
  const chartLayout = homeVisitsBarChartLayout(printStatic);
  const { containerRef: homeVisitsChartRef, dimensions: homeVisitsChartDimensions } =
    useReportChartDimensions(chartHeight);

  return (
    <HostedReportSectionLayout
      sectionDomId="home-visits"
      className="section--white report-hosted-section-body"
      style={{ background: "var(--white)", fontFamily: body }}
      banner={
        <SectionBanner title="Home Visits" iconHref="#icon_relationship_engine" />
      }
    >
      <SectionReveal delay={0.1}>
        <ReportMockFieldFrame data={data} field="rows" source={chartSource}>
        <div
          className="home-visits-card rounded-2xl p-6 md:p-8 mb-10"
          style={{ background: "#fff", border: `1px solid ${border}` }}
        >
          {chartData.length === 0 ? (
            <p className="text-sm mb-0" style={{ color: muted }}>
              No home-visit data for this reporting period yet. Use Refresh
              data in the builder after warehouse program-goal rows are
              available.
            </p>
          ) : (
            <>
          <div
            className="home-visits-legend flex flex-wrap items-center gap-x-6 gap-y-2 mb-6"
            style={{ fontFamily: FONT_BODY, fontSize: 13, color: TEXT_SECONDARY }}
          >
            {STATUS_CONFIG.map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: s.color,
                  }}
                />
                <span style={{ fontWeight: 600 }}>{s.label}</span>
              </div>
            ))}
          </div>

          <div
            ref={homeVisitsChartRef}
            className="home-visits-chart report-chart-host"
            style={{ width: "100%", minWidth: 0, height: chartHeight }}
          >
            {homeVisitsChartDimensions ? (
              <BarChart
                width={homeVisitsChartDimensions.width}
                height={homeVisitsChartDimensions.height}
                data={chartData}
                margin={chartLayout.margin}
                barGap={chartLayout.barGap}
                barCategoryGap={chartLayout.barCategoryGap}
              >
                <YAxis type="number" hide domain={[0, 'dataMax']} />
                <XAxis
                  dataKey="group"
                  axisLine={false}
                  tickLine={false}
                  padding={chartLayout.xAxisPadding}
                  tick={{
                    fontFamily: FONT_BODY,
                    fontSize: chartLayout.tickFontSize,
                    fontWeight: 500,
                    fill: TEXT_SECONDARY,
                  }}
                  interval={0}
                />
                {STATUS_CONFIG.map((s) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    fill={s.color}
                    radius={[4, 4, 0, 0]}
                    barSize={chartLayout.barSize}
                    shape={s.key === "refused" ? <SliverBar /> : undefined}
                  >
                    <LabelList
                      dataKey={s.key}
                      position="top"
                      style={{
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 500,
                        fontSize: chartLayout.labelFontSize,
                        fill: TEXT_PRIMARY,
                      }}
                    />
                  </Bar>
                ))}
              </BarChart>
            ) : null}
          </div>
            </>
          )}
        </div>
        </ReportMockFieldFrame>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default HomeVisits;
